import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import {
  IPost,
  IStructuredDataEntry,
  IStructuredDataRelated,
  IStructuredDataRelatedItem
} from 'src/types/hygraph'
import ga from 'src/utils/pixels/gtag'

export type RelatedItem = IStructuredDataRelatedItem

type IProps = {
  post: IPost
  variant?: 'help' | 'blog'
  className?: string
}

const isRelatedEntry = (
  entry: IStructuredDataEntry
): entry is IStructuredDataRelated =>
  entry?.type === 'related' && Array.isArray(entry.items)

// Help-center + per-pillar-blog topic routes. Must include every Hygraph
// topic that's used in `related.items[].topic`; missing a blog topic here
// caused the audit's "Page has links to redirect" warning (May 2026) —
// blog-topic items fell through to the FAQ fallback below and Next emitted
// a 301 from /acerca-de-spotme/faqs/{slug} to the real destination.
const TOPIC_PATH_MAP: Record<string, string> = {
  anfitrion: '/acerca-de-spotme/anfitrion',
  rentero: '/acerca-de-spotme/rentero',
  faqs: '/acerca-de-spotme/faqs',
  spotme: '/acerca-de-spotme/spotme',
  seguridad: '/acerca-de-spotme/seguridad',
  cuenta: '/acerca-de-spotme/cuenta',
  'blog-mini-bodega': '/renta-mini-bodegas/blog',
  'blog-parking': '/pension-estacionamiento/blog',
  'blog-bodega': '/renta-bodegas-comerciales/blog',
  'blog-nave': '/renta-naves-industriales/blog',
  'blog-host': '/anfitrion/blog'
}

// Help-center slugs that 301-redirect to canonical city/pillar pages. When a
// related item points to one of these (no topic / blog-topic / faqs-topic),
// short-circuit straight to the canonical destination to avoid the 301 hop
// that Ahrefs flags as "Page has links to redirect". Keep in sync with the
// matching map in scripts/hygraph/fixBrokenLinks.mjs and next.config.js.
const FAQ_SLUG_REDIRECT_SHORTCUTS: Record<string, string> = {
  'mini-bodegas-monterrey': '/renta-mini-bodegas/monterrey',
  'mejores-minibodegas-monterrey': '/renta-mini-bodegas/monterrey'
}

const buildHref = (item: RelatedItem) => {
  if (item.href) return item.href
  const shortcut = FAQ_SLUG_REDIRECT_SHORTCUTS[item.slug]
  if (shortcut) return shortcut
  if (item.topic) {
    const base = TOPIC_PATH_MAP[item.topic]
    if (base) return `${base}/${item.slug}`
  }
  return `/acerca-de-spotme/faqs/${item.slug}`
}

const RelatedArticles = ({
  post,
  variant = 'help',
  className = ''
}: IProps) => {
  const { t } = useTranslation('common')
  const sd = Array.isArray(post.structuredData) ? post.structuredData : []
  const relatedEntry = sd.find(isRelatedEntry)
  const items = relatedEntry?.items ?? []
  if (items.length === 0) return null

  const heading =
    variant === 'blog'
      ? t('HELP.RELATED_HEADING_BLOG')
      : t('HELP.RELATED_HEADING')

  return (
    <section
      aria-label={heading}
      className={`border-t border-ink-100/15 pt-10 md:pt-14 ${className}`}
    >
      <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
        {heading}
      </p>
      <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
        {items.map((item) => (
          <li key={item.slug}>
            <Link
              href={buildHref(item)}
              onClick={() =>
                ga.event('help_related_click', {
                  from_slug: post.slug ?? '',
                  to_slug: item.slug
                })
              }
              className="group block border-t border-ink-100/40 pt-4 transition-colors hover:border-blue-500"
            >
              <p className="font-display text-[16px] leading-[1.3] font-medium tracking-[-0.005em] text-ink-700 transition-colors group-hover:text-blue-500 md:text-[18px]">
                {item.label ?? item.slug}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default React.memo(RelatedArticles)
